import { shortDate } from '../../shared/lib/format';
import type { ISelectOption } from '../../shared/ui/select';
import type { TInvitation, TMember, TRole } from './api';

export const ROLE_OPTIONS: readonly ISelectOption<TRole>[] = [
  { value: 'owner', label: 'Owner', description: 'Everything, including roles and billing.' },
  { value: 'admin', label: 'Admin', description: 'Runs the pipeline and invites people.' },
  { value: 'member', label: 'Member', description: 'Reads events and works on issues.' },
];

export const roleLabel = (role: TRole): string =>
  ROLE_OPTIONS.find((option) => option.value === role)?.label ?? role;

export interface IActor {
  userId: string;
  role: TRole;
}

export const isManager = (actor: IActor | null): boolean =>
  actor !== null && (actor.role === 'owner' || actor.role === 'admin');

export const roleOptionsFor = (actor: IActor | null): readonly ISelectOption<TRole>[] =>
  actor?.role === 'owner'
    ? ROLE_OPTIONS
    : ROLE_OPTIONS.filter((option) => option.value !== 'owner');

export const ownerCount = (members: readonly TMember[]): number =>
  members.filter((member) => member.role === 'owner').length;

export const roleBlock = (actor: IActor | null, target: TMember): string | null => {
  if (!isManager(actor)) return 'Only an owner or an admin can change a role.';
  if (actor && actor.userId === target.userId) {
    return 'Nobody changes their own role, not even an owner. Ask another owner to do it.';
  }
  if (target.role === 'owner' && actor?.role !== 'owner') {
    return 'Only an owner can take the owner role away.';
  }
  return null;
};

export const removalBlock = (
  actor: IActor | null,
  target: TMember,
  owners: number,
): string | null => {
  if (!isManager(actor)) return 'Only an owner or an admin can remove somebody.';
  if (target.role === 'owner' && actor?.role !== 'owner') {
    return 'Only an owner can remove an owner.';
  }
  if (target.role === 'owner' && owners <= 1) {
    return 'The last owner stays: removing them would leave the organization with nobody who can hand the role on.';
  }
  return null;
};

export interface IDeadline {
  label: string;
  expired: boolean;
}

export const invitationDeadline = (
  invitation: TInvitation,
  now: number,
  timeZone?: string,
): IDeadline => {
  const at = new Date(invitation.expiresAt).getTime();
  if (Number.isNaN(at)) return { label: invitation.expiresAt, expired: false };

  if (at <= now)
    return { label: `Expired ${shortDate(invitation.expiresAt, timeZone)}`, expired: true };

  const hours = Math.round((at - now) / 3_600_000);
  if (hours < 24)
    return { label: `Expires in ${hours} hour${hours === 1 ? '' : 's'}`, expired: false };

  const days = Math.round(hours / 24);
  return { label: `Expires in ${days} day${days === 1 ? '' : 's'}`, expired: false };
};

export const seatLine = (members: number, invitations: number): string => {
  const people = `${members} ${members === 1 ? 'person' : 'people'}`;
  return invitations === 0 ? people : `${people} · ${invitations} invited`;
};
